import { Extension, InputRule, Node, Mark, mergeAttributes } from '@tiptap/core';

export const CustomTextAlign = Extension.create({
    name: 'textAlign',

    addOptions() {
        return {
            types: ['heading', 'paragraph'],
            alignments: ['left', 'center', 'right', 'justify'],
            defaultAlignment: 'left',
        };
    },

    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    textAlign: {
                        default: this.options.defaultAlignment,
                        parseHTML: (element) => element.style.textAlign || this.options.defaultAlignment,
                        renderHTML: (attributes) => {
                            if (attributes.textAlign === this.options.defaultAlignment) {
                                return {};
                            }

                            return {
                                style: `text-align: ${attributes.textAlign}`,
                            };
                        },
                    },
                },
            },
        ];
    },

    addCommands() {
        return {
            setTextAlign: (alignment: string) => ({ commands }: { commands: any }) => {
                if (!this.options.alignments.includes(alignment)) {
                    return false;
                }

                return this.options.types.every((type) => commands.updateAttributes(type, { textAlign: alignment }));
            },

            unsetTextAlign: () => ({ commands }: { commands: any }) => {
                return this.options.types.every((type) => commands.resetAttributes(type, 'textAlign'));
            },
        } as any;
    },
});

export const CustomTypography = Extension.create({
    name: 'typography',

    addInputRules() {
        return [
            // em dash: -- becomes —
            new InputRule({
                find: /--$/,
                handler: ({ state, range }) => {
                    const { tr } = state;
                    const start = range.from;
                    const end = range.to;
                    tr.insertText('—', start, end);
                },
            }),
            // ellipsis: ... becomes …
            new InputRule({
                find: /\.\.\.$/,
                handler: ({ state, range }) => {
                    const { tr } = state;
                    const start = range.from;
                    const end = range.to;
                    tr.insertText('…', start, end);
                },
            }),
            // right arrow: -> becomes →
            new InputRule({
                find: /->$/,
                handler: ({ state, range }) => {
                    const { tr } = state;
                    const start = range.from;
                    const end = range.to;
                    tr.insertText('→', start, end);
                },
            }),
            // left arrow: <- becomes ←
            new InputRule({
                find: /<-$/,
                handler: ({ state, range }) => {
                    const { tr } = state;
                    const start = range.from;
                    const end = range.to;
                    tr.insertText('←', start, end);
                },
            }),
            // copyright: (c) becomes ©
            new InputRule({
                find: /\(c\)$/,
                handler: ({ state, range }) => {
                    const { tr } = state;
                    const start = range.from;
                    const end = range.to;
                    tr.insertText('©', start, end);
                },
            }),
        ];
    },
});

export const CustomImage = Node.create({
    name: 'image',
    group: 'block',
    draggable: true,

    addAttributes() {
        return {
            src: {
                default: null,
            },
            alt: {
                default: null,
            },
            title: {
                default: null,
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'img[src]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['img', mergeAttributes(HTMLAttributes)];
    },

    addCommands() {
        return {
            setImage: (options: { src: string, alt?: string, title?: string }) => ({ commands }: { commands: any }) => {
                return commands.insertContent({
                    type: this.name,
                    attrs: options,
                });
            },
        } as any;
    },
});

export const CustomLink = Mark.create({
    name: 'link',

    priority: 1000,

    keepOnSplit: false,

    addAttributes() {
        return {
            href: {
                default: null,
            },
            target: {
                default: '_blank',
            },
            rel: {
                default: 'noopener noreferrer nofollow',
            },
            class: {
                default: 'text-blue-500 hover:text-blue-600 no-underline cursor-pointer',
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'a[href]:not([href^="javascript:"])',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['a', mergeAttributes(HTMLAttributes), 0];
    },

    addCommands() {
        return {
            setLink: (attributes: { href: string; target?: string }) => ({ chain }: { chain: any }) => {
                return chain()
                    .setMark(this.name, attributes)
                    .setMeta('preventAutolink', true)
                    .run();
            },
            unsetLink: () => ({ chain }: { chain: any }) => {
                return chain()
                    .unsetMark(this.name)
                    .setMeta('preventAutolink', true)
                    .run();
            },
            toggleLink: (attributes: { href: string; target?: string }) => ({ chain, editor }: { chain: any; editor: any }) => {
                if (editor.isActive(this.name)) {
                    return chain().unsetMark(this.name).run();
                }
                return chain().setMark(this.name, attributes).run();
            },
        } as any;
    },
});

export const CustomTable = Node.create({
    name: 'table',
    group: 'block',
    content: 'tableRow+',
    parseHTML() {
        return [{ tag: 'table' }];
    },
    renderHTML({ HTMLAttributes }) {
        return ['table', mergeAttributes(HTMLAttributes, { class: 'border-collapse border border-gray-300 w-full my-4' }), ['tbody', 0]];
    },
    addCommands() {
        return {
            insertTable: ({ rows = 3, cols = 3 } = {}) => ({ tr, dispatch, editor }: { tr: any, dispatch: any, editor: any }) => {
                const rowNodes = [];
                for (let i = 0; i < rows; i++) {
                    const cells = [];
                    for (let j = 0; j < cols; j++) {
                        cells.push(editor.schema.nodes.tableCell.createAndFill());
                    }
                    rowNodes.push(editor.schema.nodes.tableRow.create(null, cells));
                }
                const table = editor.schema.nodes.table.create(null, rowNodes);
                if (dispatch) {
                    tr.replaceSelectionWith(table).scrollIntoView();
                }
                return true;
            },
            deleteTable: () => ({ tr, dispatch, state }: { tr: any, dispatch: any, state: any }) => {
                const { $from } = state.selection;
                for (let d = $from.depth; d > 0; d--) {
                    if ($from.node(d).type.name === 'table') {
                        if (dispatch) {
                            tr.delete($from.before(d), $from.after(d)).scrollIntoView();
                        }
                        return true;
                    }
                }
                return false;
            },
            addRowAfter: () => ({ state, dispatch }: { state: any, dispatch: any }) => {
                const { $from } = state.selection;
                for (let d = $from.depth; d > 0; d--) {
                    if ($from.node(d).type.name === 'tableRow') {
                        if (dispatch) {
                            const row = $from.node(d);
                            const cells = [];
                            for (let i = 0; i < row.childCount; i++) {
                                cells.push(state.schema.nodes.tableCell.createAndFill());
                            }
                            const newRow = state.schema.nodes.tableRow.create(null, cells);
                            dispatch(state.tr.insert($from.after(d), newRow).scrollIntoView());
                        }
                        return true;
                    }
                }
                return false;
            },
            deleteRow: () => ({ state, dispatch }: { state: any, dispatch: any }) => {
                const { $from } = state.selection;
                for (let d = $from.depth; d > 0; d--) {
                    if ($from.node(d).type.name === 'tableRow') {
                        if (dispatch) {
                            const table = $from.node(d - 1);
                            if (table.childCount <= 1) {
                                // Last row, delete the whole table
                                dispatch(state.tr.delete($from.before(d - 1), $from.after(d - 1)).scrollIntoView());
                            } else {
                                dispatch(state.tr.delete($from.before(d), $from.after(d)).scrollIntoView());
                            }
                        }
                        return true;
                    }
                }
                return false;
            },
            addColumnAfter: () => ({ state, dispatch }: { state: any, dispatch: any }) => {
                const { $from } = state.selection;
                let cellIndex = -1;
                let tableDepth = -1;
                for (let d = $from.depth; d > 0; d--) {
                    if ($from.node(d).type.name === 'tableCell' || $from.node(d).type.name === 'tableHeader') {
                        cellIndex = $from.index(d - 1);
                    }
                    if ($from.node(d).type.name === 'table') {
                        tableDepth = d;
                        break;
                    }
                }
                if (cellIndex === -1 || tableDepth === -1) return false;
                if (dispatch) {
                    const table = $from.node(tableDepth);
                    let tr = state.tr;
                    let offset = 0;
                    table.descendants((node, pos, parent) => {
                        if (node.type.name === 'tableRow') {
                            const rowPos = $from.before(tableDepth) + pos + 1;
                            let cellOffset = 0;
                            for (let i = 0; i <= cellIndex; i++) {
                                cellOffset += node.child(i).nodeSize;
                            }
                            const newCell = state.schema.nodes.tableCell.createAndFill();
                            tr = tr.insert(rowPos + cellOffset + offset, newCell);
                            offset += newCell.nodeSize;
                        }
                        return false;
                    });
                    dispatch(tr.scrollIntoView());
                }
                return true;
            },
            deleteColumn: () => ({ state, dispatch }: { state: any, dispatch: any }) => {
                const { $from } = state.selection;
                let cellIndex = -1;
                let tableDepth = -1;
                for (let d = $from.depth; d > 0; d--) {
                    if ($from.node(d).type.name === 'tableCell' || $from.node(d).type.name === 'tableHeader') {
                        cellIndex = $from.index(d - 1);
                    }
                    if ($from.node(d).type.name === 'table') {
                        tableDepth = d;
                        break;
                    }
                }
                if (cellIndex === -1 || tableDepth === -1) return false;
                if (dispatch) {
                    const table = $from.node(tableDepth);
                    const row = table.child(0);
                    if (row.childCount <= 1) {
                        // Last column, delete the whole table
                        dispatch(state.tr.delete($from.before(tableDepth), $from.after(tableDepth)).scrollIntoView());
                        return true;
                    }
                    let tr = state.tr;
                    let offset = 0;
                    table.descendants((node, pos, parent) => {
                        if (node.type.name === 'tableRow') {
                            const rowPos = $from.before(tableDepth) + pos + 1;
                            let cellOffset = 0;
                            for (let i = 0; i < cellIndex; i++) {
                                cellOffset += node.child(i).nodeSize;
                            }
                            const cellToDelete = node.child(cellIndex);
                            tr = tr.delete(rowPos + cellOffset + offset, rowPos + cellOffset + offset + cellToDelete.nodeSize);
                            offset -= cellToDelete.nodeSize;
                        }
                        return false;
                    });
                    dispatch(tr.scrollIntoView());
                }
                return true;
            },
        } as any;
    },
});

export const CustomTableRow = Node.create({
    name: 'tableRow',
    content: '(tableCell | tableHeader)*',
    parseHTML() {
        return [{ tag: 'tr' }];
    },
    renderHTML() {
        return ['tr', 0];
    },
});

export const CustomTableCell = Node.create({
    name: 'tableCell',
    content: 'block+',
    parseHTML() {
        return [{ tag: 'td' }];
    },
    renderHTML({ HTMLAttributes }) {
        return ['td', mergeAttributes(HTMLAttributes, { class: 'border border-gray-400 p-2 min-w-[50px]' }), 0];
    },
});

export const CustomTableHeader = Node.create({
    name: 'tableHeader',
    content: 'block+',
    parseHTML() {
        return [{ tag: 'th' }];
    },
    renderHTML({ HTMLAttributes }) {
        return ['th', mergeAttributes(HTMLAttributes, { class: 'border border-gray-400 p-2 bg-muted font-bold min-w-[50px] text-left' }), 0];
    },
});
